import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Typography,
} from "@mui/material";
import { getDoc } from "firebase/firestore";
import { findIndex, forEach, some } from "lodash";
import moment, { Moment } from "moment-timezone";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import UsuarioDb from "../../../../db/usuarioDb";
import Functions from "../../../../callable/functions";
import { useSnackBar } from "../../../../stores/snackBarStores";

type Props = {
  open: boolean;
  onClose: () => void;
  usuarioId: string;
};

// Primeiro ponto de partida possível no internacional: a Lei 14.754/2023 vale
// a partir de 01/01/2024, então a custódia inicial mais antiga é 31/12/2023.
const PRIMEIRO_PONTO_DE_PARTIDA = "2023-12-31";

export default function AlterarPontoDePartidaInternacionalDialog({
  open,
  onClose,
  usuarioId,
}: Props) {
  const navigate = useNavigate();
  const dispatchSnack = useSnackBar();
  const [mesesSlider, setMesesSlider] = useState<Moment[]>([moment()]);
  const [mesSelecionado, setMesSelecionado] = useState<Moment>();
  const [marks, setMarks] = useState<{ value: number; label: string }[]>([]);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!!usuarioId) {
      getDoc(new UsuarioDb().doc({ usuarioId })).then((doc) => {
        const usuario = doc.data();
        if (!!usuario) {
          const ontem = moment.tz("America/Sao_Paulo").subtract(1, "day");
          let dataIterada = moment.tz(
            PRIMEIRO_PONTO_DE_PARTIDA,
            "America/Sao_Paulo"
          );
          // tz fixo em SP: o valor gravado é 00:00 SP e, em navegador fora do
          // Brasil, o mês/dia local podia cair no vizinho.
          setMesSelecionado(
            usuario.pontoDePartidaInternacional
              ?.clone()
              .tz("America/Sao_Paulo") ?? dataIterada.clone()
          );
          let meses: Moment[] = [];
          while (dataIterada.isBefore(ontem)) {
            if (dataIterada.isSame(dataIterada.clone().endOf("month"), "day")) {
              meses.push(dataIterada.clone());
            }
            dataIterada.add(1, "day");
          }
          setMesesSlider(meses);
        }
      });
    }
  }, [usuarioId]);

  useEffect(() => {
    const finsDeAno: { value: number; label: string }[] = [];
    forEach(mesesSlider, (m, index) => {
      if (m.isSame(m.clone().endOf("year"), "month")) {
        finsDeAno.push({
          value: index,
          label: m.format("MMM/YY"),
        });
      }
    });
    setMarks(finsDeAno);
  }, [mesesSlider]);

  const salvarPontoDePartida = async () => {
    if (!mesSelecionado) return;
    setSalvando(true);
    try {
      await new Functions().alterarPontoDePartidaInternacional({
        data: mesSelecionado.format("YYYY-MM-DD"),
      });
      dispatchSnack({
        type: "ALERT",
        payload: {
          mensagem: `Ponto de partida internacional movido para ${mesSelecionado.format(
            "DD/MM/YYYY"
          )}. Preencha a custódia e o prejuízo acumulado nessa data e recalcule.`,
          severidade: "success",
        },
      });
      onClose();
      // Leva direto para o formulário: sem posição cadastrada a timeline
      // internacional só tem a aba Ponto de Partida.
      navigate("/internacional#ponto-de-partida");
    } catch (error) {
      console.error(error);
      dispatchSnack({
        type: "ALERT",
        payload: {
          mensagem:
            "Não foi possível alterar o ponto de partida internacional. Tente novamente.",
          severidade: "error",
        },
      });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle color="text.primary" sx={{ typography: "h3" }}>
        Definir ponto de partida internacional em{" "}
        <Typography variant="dataMono" fontSize="inherit" fontWeight={500}>
          {mesSelecionado?.format("MMM/YY")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" px={4} pt={1}>
          A custódia inicial, o prejuízo acumulado e todos os meses
          internacionais já calculados serão descartados. Os statements
          importados são mantidos, mas só as operações a partir da nova data
          entram no cálculo.
        </Typography>
        <Box p={4}>
          <Slider
            aria-label="Ponto de partida internacional"
            track="inverted"
            valueLabelDisplay="on"
            sx={(theme) => ({
              // Marca e balão do slider em mono tabular.
              "& .MuiSlider-markLabel, & .MuiSlider-valueLabel": {
                ...theme.typography.dataMono,
                fontSize: 11,
              },
              "& .MuiSlider-valueLabel": {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
              },
            })}
            value={findIndex(mesesSlider, (mes) =>
              mes.isSame(mesSelecionado, "month")
            )}
            onChange={(_e, value) =>
              setMesSelecionado(mesesSlider[value as number])
            }
            min={0}
            max={mesesSlider.length - 1}
            getAriaLabel={(m) => mesesSlider[m].format("MMM/YY")}
            getAriaValueText={(m) => mesesSlider[m].format("MMM/YY")}
            valueLabelFormat={(m) => mesesSlider[m].format("MMM/YY")}
            marks={some(marks) ? marks : []}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} color="inherit">
          Cancelar
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={salvando}
          onClick={salvarPontoDePartida}
        >
          Salvar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
